import type { DateRange, Matcher } from "react-day-picker";

import * as React from "react";

import DeprecatedButton from "@v1/components/designSystem/button/Button";
import Calendar from "@v1/components/designSystem/shadcn/core/calendar";
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from "@v1/components/designSystem/shadcn/core/popover";
import clsxMerge from "@v1/components/designSystem/shadcn/utils/cn-merge";
import { addDays } from "date-fns";
import { CalendarDays } from "lucide-react";
import { dateRangeFormatter } from "replo-utils/lib/datetime";
import { useControllableState } from "replo-utils/react/use-controllable-state";

// NOTE (Kurt, 2024-11-22): We use the popover align offset to vertically align the date picker
// with the trigger button.
const POPOVER_ALIGN_OFFSET = -15;
// NOTE (Kurt, 2024-11-22): We use the side offset to horizontally align the date picker
// with the trigger button. This is the horizontal distance between the date picker and
// the trigger button.
const POPOVER_SIDE_OFFSET = 20;

type DatePickerWithRangeProps = React.HTMLAttributes<HTMLDivElement> & {
  isDisabled?: boolean;
  popoverTriggerComponent?: () => React.ReactNode;
  initialDateRange?: DateRange;
  customTrigger?: React.ReactNode;
  triggerOnHover?: boolean;
  value?: DateRange;
  onChange?: (value: DateRange) => void;
  updateAction: (dateRange: DateRange) => void;
  closeAction?: () => void;
  excludeDisabledDates?: boolean;
  disabledDates?: Matcher | Matcher[];
  rangeExtendsOnSelect?: boolean;
};

export function DatePickerWithRange({
  className,
  isDisabled,
  popoverTriggerComponent,
  initialDateRange,
  triggerOnHover = false,
  value: controlledValue,
  onChange: onControlledChange,
  updateAction,
  closeAction,
  excludeDisabledDates = false,
  disabledDates,
  rangeExtendsOnSelect = true,
}: DatePickerWithRangeProps) {
  const [open, setOpen] = React.useState(false);

  const [date, setDate] = useControllableState(
    controlledValue,
    initialDateRange ?? {
      from: addDays(new Date(), -7),
      to: new Date(),
    },
    onControlledChange,
  );

  const validDateRange = Boolean(date.from && date.to);

  const handleMouseEnter = () => {
    if (triggerOnHover) {
      setOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (triggerOnHover) {
      setTimeout(() => {
        setOpen(false);
      }, 100);
    }
  };

  const defaultTrigger = (
    <button
      className={clsxMerge(
        "border-0.5 border-border bg-surface text-default flex h-8 items-center justify-center rounded-sm p-2",
        isDisabled && "cursor-not-allowed opacity-50",
        className,
      )}
    >
      <div className="flex items-center justify-center gap-[8px]">
        <CalendarDays className="h-4 w-4" />
        {date?.from ? (
          dateRangeFormatter(date)
        ) : (
          <span className="text-default">Pick a date</span>
        )}
      </div>
    </button>
  );

  return (
    <div className={clsxMerge("grid gap-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            {popoverTriggerComponent
              ? popoverTriggerComponent()
              : defaultTrigger}
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="mb-2 w-auto rounded-[12px] p-0"
          align="start"
          side="right"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          avoidCollisions={true}
          sideOffset={POPOVER_SIDE_OFFSET}
          alignOffset={POPOVER_ALIGN_OFFSET}
        >
          <Calendar
            mode="range"
            defaultMonth={date?.from}
            selected={date}
            onSelect={
              !rangeExtendsOnSelect
                ? (nextRange, selectedDay) => {
                    if (nextRange) {
                      // NOTE (Kurt, 2024-09-10): This enables us to reset the date range whenever we
                      // select a date with a range already selected.
                      setDate((range) => {
                        if (range.from && range.to) {
                          return { from: selectedDay, to: undefined };
                        }
                        return nextRange as DateRange;
                      });
                    } else {
                      setDate({ from: selectedDay, to: selectedDay });
                    }
                  }
                : (newDate) => {
                    setDate(newDate as DateRange);
                  }
            }
            numberOfMonths={2}
            required={false}
            excludeDisabled={excludeDisabledDates}
            disabled={disabledDates}
          />
          <div className="border-border flex h-[52px] w-full items-center justify-between border-t px-2.5 py-3">
            <div className="flex items-center gap-[8px]">
              <div className="text-muted text-sm">Selected period</div>
              <div className="text-default text-sm font-semibold">
                {date?.from ? (
                  dateRangeFormatter(date)
                ) : (
                  <span className="text-default">Pick a date</span>
                )}
              </div>
            </div>
            <div className="flex items-center justify-center gap-[8px]">
              <PopoverClose asChild>
                <DeprecatedButton
                  variant="secondary"
                  size="base"
                  onClick={closeAction}
                >
                  Cancel
                </DeprecatedButton>
              </PopoverClose>
              <DeprecatedButton
                variant="primary"
                size="base"
                onClick={() => validDateRange && updateAction(date)}
                disabled={!validDateRange}
                tooltipText={validDateRange ? undefined : "Select a date range"}
              >
                Update
              </DeprecatedButton>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
